import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { degreesToRadians } from './../helpers/getAngleConversions';
import { Colors } from '@blueprintjs/core';

/**
 * @param arcRadii The radii of the inner and outer portions of the arc.
 * @param [colors] An object containing information for coloring the meter.
 * @param [colors.fullFill] Indicates if colors will fill from start angle up to needle or to end angle (true).
 * @param [colors.interpolator] A function which returns a color string value to be used at a given segment (see D3).
 * @param [colors.reverse] Indicates if color scheme should be the reverse of the normal interpolator
 * @param [colors.segments] A number indicating how many segments of the color scale to show. More segments will result in a smoother gradient.
 * @param [colors.score] A number representing the percentage of the arc where the gradient stops. Ignored if fullFull is true.
 * @param position The relative position of this SVG g element
 * @param segments An array of value color pairs indicating the ratio of a given color to other colors in the arc.
 *
 * @returns A SVG G element representing an arc with colored segments corresponding to the supplied data
 */
export const MeterArc = (
	(
		{
			arcRadii: { inner, outer },
			colors: { fullFill, interpolator, reverse, score, segments },
			position,
		}: MeterArcProps,
	) => {
		
	const ref = useRef<SVGGElement>(null);
	
		useEffect(() => {
			/*
			 *   Gernerate the path angles needed by arc() from data
			 *   This will give an appropriate portion (based on value) of the full arc to each color
			 */
			const angleGen = d3
				.pie<any>()
				.startAngle(degreesToRadians(270))
				// Allow for clockwise wrapping of angles when start angle is greater than end angle
				.endAngle(degreesToRadians(450))
				//.value((d) => d.value)
				.sort(null);

			const ary = Array(segments).fill(1);
			const angles = angleGen(ary);
			const arcGen = d3.arc<any, any>().innerRadius(inner).outerRadius(outer);

			/* For use later with app colors? 
			const colorMap = segments.map((cv) => cv.color);
			const interpolater = d3.piecewise(colorMap); 
			*/

			const getColor = (i) => interpolator((reverse ? segments - 1 - i : i) / ary.length);
			const color = (_d, i) => (!fullFill && i / segments > score ? Colors.GRAY3 : getColor(i));

			/* Draw the arc */
			d3.select(ref.current)
				.selectAll('path')
				.data(angles)
				.join('path')
				.attr('d', arcGen)
				.attr('stroke', color)
				.attr('fill', color);
		}, [fullFill, inner, interpolator, outer, ref, reverse, score, segments]);

		return <g ref={ref} transform={`translate(${position.x}, ${position.y})`} />;
	}
);

export interface ArcColorProps {
	fullFill?: boolean;
	interpolator?: (t: number) => string;
	reverse?: boolean;
	score?: number;
	segments?: number;
}

export interface MeterArcProps {
	arcRadii: { inner: number; outer: number };
	colors?: ArcColorProps;
	position: { x: number; y: number };
}
